import React, { useEffect } from "react";

import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { Detector } from "react-detect-offline";
import classNames from "classnames";
import _ from "lodash";

import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import { Badge, Tabs, IconButton } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import withPusherBind from "UIComponents/PusherConsumerHoc";

import { useDispatch } from "Hooks/useDispatch";

import {
  MESSAGING_FILTERS,
  getUnreadChatsCountQueryVariables,
  MESSAGING_FILTERS_CONSTANTS,
  MESSAGING_FILTER_PREFERENCE_CONFIG,
  isMessagingPublicChannelEnabled,
  isMessagingDepartmentChannelEnabled,
  recordThreadListV2TabChange,
} from "MessagingV2/modules/Utils";
import { getOrganizationId, getUserType } from "modules/CommonSelectors";
import { generateConfigurationQueryVariablesMemoized } from "ChildrenPage/modules/ChildrenPageUtils";
import { parseConfigurationValue } from "Utils";
import {
  getMessagingV2ReduxState,
  setRevampV2FilterThread,
} from "MessagingV2/modules/MessagingModule";

import classes from "./ThreadV2ListFilter.scss";
import { getChatUnreadCountQuery } from "../Utils/ThreadListQueries";
import { getPreferenceQuery } from "ChildrenPage/modules/ChildrenPageQuery";
import { STAFF } from "Constants/stringConstants";
import { getPreferencesFromCacheV2 } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import ColumnConfigureButton from "./ColumnConfigureButton/ColumnConfigureButton";

const styles = {
  modalBody: {
    paddingTop: "0px",
    paddingRight: "0px",
    paddingBottom: "0px",
  },
  tabContainerStyle: {
    paddingBottom: "8px",
  },
};

function shouldShowFilter({ showFilter, filterKey, filterDisplayOrder }) {
  const isPublicChannelEnable = isMessagingPublicChannelEnabled();

  const isDepartmentChannelEnable = isMessagingDepartmentChannelEnabled();

  const { PUBLIC_CHANNEL, DEPARTMENT_CHANNEL } = MESSAGING_FILTERS_CONSTANTS;

  if (_.includes(showFilter, filterKey)) {
    return true;
  }

  if (
    isPublicChannelEnable &&
    _.isEqual(PUBLIC_CHANNEL, filterKey) &&
    !_.includes(filterDisplayOrder, filterKey)
  ) {
    return true;
  }

  if (
    isDepartmentChannelEnable &&
    _.isEqual(DEPARTMENT_CHANNEL, filterKey) &&
    !_.includes(filterDisplayOrder, DEPARTMENT_CHANNEL)
  ) {
    return true;
  }

  return false;
}

const getMessagingFilterButtons = ({ filterPreference, t, countObj }) => {
  const { filterDisplayOrder = [], showFilter = [] } = filterPreference;

  const isPublicChannelEnable = isMessagingPublicChannelEnabled();

  const isDepartmentChannelEnable = isMessagingDepartmentChannelEnabled();

  const updatedFilterWithDepartment = isDepartmentChannelEnable
    ? _.union(filterDisplayOrder, [
        MESSAGING_FILTERS_CONSTANTS.DEPARTMENT_CHANNEL,
      ])
    : _.without(
        filterDisplayOrder,
        MESSAGING_FILTERS_CONSTANTS.DEPARTMENT_CHANNEL
      );

  const updatedFilterDisplayOrder = isPublicChannelEnable
    ? _.union(updatedFilterWithDepartment, [
        MESSAGING_FILTERS_CONSTANTS.PUBLIC_CHANNEL,
      ])
    : _.without(
        updatedFilterWithDepartment,
        MESSAGING_FILTERS_CONSTANTS.PUBLIC_CHANNEL
      );

  const filteredDisplayOrder = _.filter(
    updatedFilterDisplayOrder,
    filterKey =>
      _.has(MESSAGING_FILTERS, filterKey) &&
      _.includes(
        _.get(MESSAGING_FILTERS, [filterKey, "visibleUserTypes"], []),
        getUserType()
      )
  );

  return _.map(filteredDisplayOrder, filterKey => {
    const filter = MESSAGING_FILTERS[filterKey];
    return {
      id: filterKey,
      value: t(_.get(filter, "label")),
      count: _.get(countObj, _.get(filter, "countKey"), null),
      disabled: _.get(filter, "disabled", false),
      isSelected: shouldShowFilter({
        showFilter,
        filterKey,
        filterDisplayOrder,
      }),
    };
  });
};

const ThreadV2Filter = props => {
  const {
    filterPreference,
    refetchMessagingV2UnreadCount,
    unreadChatCounts,
    selectedThreadFilter,
    hideThreadFilter,
    showManageFilter = true,
    testId,
  } = props;

  const componentTestId = testId
    ? `${testId}-threadV2Filter`
    : "messaging-threadV2Filter";

  const { t } = useI18n(["common"]);
  const tabsRef = React.useRef(null);
  const dispatch = useDispatch();

  const [scrollButtonState, setScrollButtonState] = React.useState({
    left: false,
    right: false,
  });

  useEffect(() => {
    if (_.isEmpty(selectedThreadFilter)) {
      dispatch(
        setRevampV2FilterThread(
          _.get(
            filterPreference,
            "filterDisplayOrder.0",
            MESSAGING_FILTERS_CONSTANTS.ALL
          )
        )
      );
    }
  }, []);

  useEffect(() => {
    const element = tabsRef.current;
    if (!element) return;

    const handleResizeOrScroll = () => {
      if (element) {
        const scrollLeft = element.scrollLeft;
        const clientWidth = element.clientWidth;
        const scrollWidth = element.scrollWidth;
        setScrollButtonState({
          left: scrollLeft > 0,
          right: scrollLeft + clientWidth < scrollWidth,
        });
      }
    };

    handleResizeOrScroll();

    const resizeObserver = new ResizeObserver(handleResizeOrScroll);

    // handle element resize or scroll with gesture
    resizeObserver.observe(element);
    element.addEventListener("scroll", handleResizeOrScroll);

    return () => {
      resizeObserver.disconnect();
      element.removeEventListener("scroll", handleResizeOrScroll);
    };
  }, [tabsRef, filterPreference]);

  if (!filterPreference || hideThreadFilter) return null;

  const messagingFilterButtons = getMessagingFilterButtons({
    filterPreference,
    t,
    countObj: {
      unreadChatCounts,
    },
  });

  const handleNetworkChange = isOnline => {
    if (isOnline && _.isFunction(refetchMessagingV2UnreadCount))
      refetchMessagingV2UnreadCount();
  };

  const tabs = _.reduce(
    messagingFilterButtons,
    (result, filter) => {
      const { id, value: label, isSelected, count } = filter;

      if (!isSelected) return result;

      result.push({
        value: id,
        label,
        suffix: count ? (
          <Badge
            dsVersion="2.0"
            value={count}
            placement="left"
            type="numeric"
            size="xxx-small"
            shape="circle"
          />
        ) : null,
      });

      return result;
    },
    []
  );

  const scrollTabToLeft = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollLeft -= 100;
    }
    // hide left scroll button if no scroll left
    if (tabsRef.current.scrollLeft <= 0) {
      setScrollButtonState(prev => ({ ...prev, left: false }));
    }
  };
  const scrollTabToRight = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollLeft += 100;
    }
    // hide right scroll button if no scroll right
    if (
      tabsRef.current?.scrollLeft + tabsRef.current?.clientWidth >=
      tabsRef.current?.scrollWidth
    ) {
      setScrollButtonState({ ...scrollButtonState, right: false });
    }
  };

  return (
    <>
      <Detector
        polling={false}
        render={() => null}
        onChange={handleNetworkChange}
      />
      <div className={classNames(classes.filterContainer)}>
        <div className={classes.tabsAndIconContainer}>
          {scrollButtonState.left ? (
            <IconButton
              icon={<ChevronLeftOutlined />}
              className={classNames(
                classes.scrollButton,
                classes.scrollButtonLeft
              )}
              dsVersion="2.0"
              type="outlined"
              size="small"
              variant="neutral"
              onClick={scrollTabToLeft}
              testId={`${componentTestId}-scrollLeft`}
            />
          ) : null}
          <div className={classes.tabsContainer} ref={tabsRef}>
            <Tabs
              options={tabs}
              onChange={value => {
                recordThreadListV2TabChange(value);
                dispatch(setRevampV2FilterThread(value));
              }}
              value={selectedThreadFilter}
              variant="inline"
              hasSeparator={false}
              containerStyle={styles.tabContainerStyle}
              dsVersion="2.0"
              testId={componentTestId}
            />
          </div>
          {scrollButtonState.right ? (
            <IconButton
              icon={<ChevronRightOutlined />}
              className={classNames(
                classes.scrollButton,
                classes.scrollButtonRight
              )}
              dsVersion="2.0"
              type="outlined"
              size="small"
              variant="neutral"
              onClick={scrollTabToRight}
              testId={`${componentTestId}-scrollRight`}
            />
          ) : null}
        </div>
        {showManageFilter && _.isEqual(getUserType(), STAFF) ? (
          <ColumnConfigureButton filterButtons={messagingFilterButtons} />
        ) : null}
      </div>
    </>
  );
};

const getConfigurationEnhancerCb = (configurationResponse = {}) => {
  const configurations = _.get(configurationResponse, "0.configurations", []);
  const config = _.find(configurations, {
    configuration: MESSAGING_FILTER_PREFERENCE_CONFIG,
  });
  const filterPreference = parseConfigurationValue(config);
  return filterPreference;
};

const mapStateToProps = state => {
  const messagingState = getMessagingV2ReduxState(state);

  const selectedThreadFilter = _.get(
    messagingState,
    "messageRevampV2FilterState.threadFilter"
  );

  // searchFilter: "",
  //   isSearchFocused: false,

  const searchFilter = _.get(
    messagingState,
    "messageRevampV2FilterState.searchFilter",
    ""
  );
  const isSearchFocused = _.get(
    messagingState,
    "messageRevampV2FilterState.isSearchFocused",
    false
  );

  const activeTab = _.get(state, "login.activeTab", "");
  const isAdminPage = activeTab === "admin";

  const configurationQueryVariables = generateConfigurationQueryVariablesMemoized(
    {
      src: "ORGANIZATION_LEVEL",
      organizationId: getOrganizationId(state),
      configurations: [MESSAGING_FILTER_PREFERENCE_CONFIG],
    }
  );

  const hideThreadFilter = !_.isEmpty(searchFilter) || isSearchFocused;

  return {
    hideThreadFilter,
    selectedThreadFilter,
    configurationQueryVariables,
    isAdminPage,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getChatUnreadCountQuery, {
    name: "getChatUnreadCount",
    options: () => ({
      fetchPolicy: "cache-and-network",
      variables: getUnreadChatsCountQueryVariables(),
      context: { useHttpLink: true },
    }),
    props({ getChatUnreadCount }) {
      const unreadChatCounts = _.get(
        getChatUnreadCount,
        "node.chats.allUnreadCount",
        {}
      );

      return {
        unreadChatCounts,
        onPusherEventTriggered: eventData => {
          if (eventData?.event === "chatMessageReceived") {
            getChatUnreadCount.refetch();
          }
        },
        refetchMessagingV2UnreadCount: getChatUnreadCount.refetch,
      };
    },
  }),
  graphql(getPreferenceQuery, {
    name: "getPreference",
    options: props => {
      return {
        variables: _.get(props, "configurationQueryVariables"),
      };
    },
    props: params => {
      const { ownProps } = params;
      const configurationsResponse = getPreferencesFromCacheV2(
        _.get(ownProps, "configurationQueryVariables")
      );
      const filterPreference = getConfigurationEnhancerCb(
        configurationsResponse,
        _.get(ownProps, "isAdminPage")
      );

      return {
        filterPreference,
      };
    },
  }),
  withPusherBind(["chatMessageReceived"])
)(ThreadV2Filter);
